import { startTransition, useCallback, useEffect, useRef, useState } from 'react'
import { Decoder } from '@msgpack/msgpack'
import PQueue from 'p-queue'

import { type IDB } from '@/core/db/db'
import { STORAGE_KEYS } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'

import DbWorker from './db-worker?worker'
import type { DbWorkerAction, DbWorkerClient, WorkerNewDataResponse, WorkerNotifResponse } from './types'

const msgpackDecoder = new Decoder()
const MIN_TIMEOUT_MS = 13

//--------------------------------------------------------------------------------------//
//                                  Worker Instances                                    //
//--------------------------------------------------------------------------------------//

enum WORKER_STATUS {
  PENDING = 'PENDING',
  INITIALIZED = 'INITIALIZED',
  READY = 'READY',
  ERROR = 'ERROR',
}

/**
 * !! DO NOT TOUCH !! - this is the final implementation of useDbWorker!
 *
 * Custom hook to handle dbWorker instance and status
 */
export function useDbWorker() {
  // State & Refs
  const dbClientRef = useRef<IDB | undefined>()
  const workerRef = useRef<DbWorkerClient | undefined>()
  const initStartedRef = useRef(false)
  const [workerStatus, setWorkerStatus] = useState<WORKER_STATUS>(WORKER_STATUS.PENDING)
  const [loadedTables, setLoadedTables] = useState<Set<string>>(new Set())
  const [snapshotLoadingSet, setSnapshotLoadingSet] = useState<Set<string>>(new Set())
  const requestedTablesRef = useRef<Set<string>>(new Set())
  const queueRef = useRef<PQueue | undefined>(new PQueue({ concurrency: 1 }))
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()

  const [wasmProcessing, setWasmProcessing] = useState(false)
  const [tablesLoadingMap, setTablesLoadingMap] = useState<Map<string, boolean>>(new Map())

  /**
   * Handle process `new_data` | `end_data` | `col_data`
   */
  const handleProcessData = useCallback((meta: string, response: WorkerNewDataResponse) => {
    if (!dbClientRef.current || !queueRef.current) return

    // format: {eventType}:{chunk_size}:{start_row}-{end_row}:{nextOrEnd}
    const [eventType, chunkSize, range, nextOrEnd] = meta.split(':')

    // convert range e.g "0-6" -> [0, 6]
    const [startRow, endRow] = range.split('-').map(Number)
    const chunkIndex = startRow / Number(chunkSize)

    // the finished flag will is last chunk of end_data event
    const isLastChunk = nextOrEnd === 'end'
    const isFinished = isLastChunk && eventType === 'end_data'

    // add this chunk processing into the queue
    queueRef.current.add(
      async () => {
        const table = dbClientRef.current?.tablesMap.get(response[0])
        if (!table) return Promise.resolve()

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // run chunk processing in a timeout to avoid blocking the main thread
        return new Promise<void>((resolve) => {
          timeoutRef.current = setTimeout(() => {
            const [tableId, lang, rows, rowsCount, resolvedColumnId, resolvedMeta, resolvedType, colInfo] = response

            switch (eventType) {
              case 'col_data':
                table.SetDbWorkerColumnData(
                  rows,
                  lang,
                  '',
                  resolvedColumnId,
                  resolvedMeta,
                  startRow,
                  endRow,
                  isLastChunk
                )
                break
              default:
                table.SetDbWorkerData(rows, rowsCount, lang, '', startRow, endRow, colInfo, isFinished)
                break
            }

            // mark the table as loaded
            if (isLastChunk && requestedTablesRef.current.has(tableId)) {
              requestedTablesRef.current.delete(tableId)
              startTransition(() => {
                setLoadedTables((prev) => {
                  if (prev.has(tableId)) return prev // skip if its already added
                  return new Set(prev).add(tableId)
                })
              })
            }

            if (isFinished) {
              window.prettyLog(`[Worker] - ${eventType}`, {
                tableName: table.settings._Name,
                tableId,
                lang,
                ...(import.meta.env.DEV ? { rows } : {}),
                rowsCount,
                colInfo,
              })

              // emit event
              window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_DATA_LOADED, { detail: { tableId } }))

              // for snapshot tables, remove from loading set
              startTransition(() => {
                if (table.isSnapshot || table.isQuery) {
                  setSnapshotLoadingSet((prev) => {
                    if (!prev.has(tableId)) return prev // skip if its already removed
                    const newSet = new Set(prev)
                    newSet.delete(tableId)
                    return newSet
                  })
                }
              })
            }

            resolve()
            //
          }, MIN_TIMEOUT_MS) // ~ minimum browser timeout, ideally it should faster than 16ms, which is 1fps (1000/60)ms
        })
      },
      { priority: -chunkIndex } // use negative chunkIndex so chunk 0 has highest priority
    )
  }, [])

  /**
   * Handle process notif
   *
   * @todo:
   * - proper loading progress for table data load
   */
  const handleProcessNotif = useCallback((response: WorkerNotifResponse) => {
    if (!dbClientRef.current || !queueRef.current) return

    const [name, description, status, progress, meta, timestamp] = response

    queueRef.current.add(async () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // run chunk processing in a timeout to avoid blocking the main thread
      return new Promise<void>((resolve) => {
        timeoutRef.current = setTimeout(() => {
          // if (import.meta.env.DEV) {
          //   window.prettyLog(
          //     `[Worker] - notif - ${status}`,
          //     { name, description, status, progress, meta, timestamp },
          //     '#954C2E'
          //   )
          // }

          switch (status) {
            case 'table-data-fetched': {
              const tableId = meta?.table_id || ''
              const tableName = dbClientRef.current?.tablesMap.get(tableId)?.settings._Name || ''
              dbClientRef.current?.fetchedTableIds.add(tableId)
              if (import.meta.env.DEV) {
                window.prettyLog(`[Worker] - notif - ${status}`, [tableName, tableId], '#954C2E')
              }
              break
            }
            case 'process-working': {
              const tableId = meta?.table_id || ''
              startTransition(() => {
                setTablesLoadingMap((prev) => {
                  const map = new Map(prev)
                  map.set(tableId, true)
                  return map
                })
                setWasmProcessing(true)
              })
              break
            }
            case 'process-success': {
              const tableId = meta?.table_id || ''
              startTransition(() => {
                setTablesLoadingMap((prev) => {
                  const map = new Map(prev)
                  map.delete(tableId)
                  return map
                })
                setWasmProcessing(false)
              })
              break
            }
            default:
              break
          }

          resolve()
          //
        }, MIN_TIMEOUT_MS) // ~ minimum browser timeout, ideally it should faster than 16ms, which is 1fps (1000/60)ms
      })
    })
  }, [])

  /**
   * Handle data received from the worker
   */
  const handleMessage = useCallback(
    async (e: MessageEvent) => {
      /**
       * Handle buffer data (Uint8Array)
       * @eventType new_data | end_data | col_data
       */
      if (e.data instanceof Uint8Array) {
        // decode the data
        const unpacked = msgpackDecoder.decode(e.data)
        if (!Array.isArray(unpacked) || unpacked.length < 2) return
        const [meta, response] = unpacked

        if (meta === 'notif') {
          handleProcessNotif(response)
          return
        }

        if (meta.startsWith('new_data') || meta.startsWith('end_data') || meta.startsWith('col_data')) {
          handleProcessData(meta, response)
          return
        }
      }

      switch (e.data.type) {
        case 'error': {
          window.prettyLog('WASM load error:', e.data.message, 'red')
          setWorkerStatus(WORKER_STATUS.ERROR)
          break
        }
        case 'get_token': {
          workerRef.current?.postMessage({
            type: 'get_token',
            payload: localStorage.getItem(STORAGE_KEYS.USER_TOKEN) || '',
          })
          break
        }
        default: {
          break
        }
      }
    },
    [handleProcessData, handleProcessNotif]
  )

  /**
   * Handle init message
   */
  const handleInitMessage = useCallback((e: MessageEvent, worker: DbWorkerClient, dbClient: IDB) => {
    switch (e.data.type) {
      case 'error': {
        window.prettyLog('WASM load error:', e.data.message, 'red')
        setWorkerStatus(WORKER_STATUS.ERROR)
        break
      }
      case 'get_token': {
        worker.postMessage({
          type: 'get_token',
          payload: localStorage.getItem(STORAGE_KEYS.USER_TOKEN) || '',
        })
        break
      }
      case 'wasm-loaded': {
        window.prettyLog(e.data.message)

        // register queue if not exist
        if (!queueRef.current) {
          queueRef.current = new PQueue({ concurrency: 1 })
        }

        // send tables infos to worker, it should be sorted where the query tables comes last (after source table)
        // so that dbWorker can process it in the correct order
        const sortedTables = [...Array.from(dbClient.tablesMap.values())]
          .sort((a, b) => Number(a.isQuery) - Number(b.isQuery))
          .map((tb) => tb.settings)

        // set tables infos
        window.prettyLog(`[Worker] - set_tables_infos (${sortedTables.length})`)
        worker.postMessage({
          type: 'set_tables_infos',
          payload: sortedTables,
        })

        // attach sendAction to the worker
        worker.sendAction = (action: DbWorkerAction) => {
          let logInfo: any = action

          if (action.type === 'get_table_data') {
            const tableId = action.payload.table_id

            // skip if the table is already requested
            if (requestedTablesRef.current.has(tableId)) return
            requestedTablesRef.current.add(tableId)

            // add table name to the log info
            const tableName = dbClient.tablesMap.get(tableId)?.settings._Name
            logInfo = [tableName, tableId, action.payload.language, action.meta?.from]

            // // add to tables loading map
            // setTablesLoadingMap((prev) => {
            //   const map = new Map(prev)
            //   map.set(tableId, true)
            //   return map
            // })
          }

          window.prettyLog(`[Worker] - ${action.type}`, logInfo)
          worker.postMessage(action)
        }

        // attach worker to the dbClient
        dbClient.SetDbWorker(worker)

        // set dbClient ref
        dbClientRef.current = dbClient

        // set worker ref
        workerRef.current = worker

        // set worker status to ready
        setWorkerStatus(WORKER_STATUS.READY)

        // attach to global window
        window.dbWorker = worker

        break
      }
      default: {
        break
      }
    }
  }, [])

  /**
   * Handle init worker
   */
  const handleInitWorker = useCallback(
    (dbClient: IDB) => {
      // prevent worker from being initialized twice
      if (initStartedRef.current) return
      initStartedRef.current = true

      // create new worker
      const worker = new DbWorker() as DbWorkerClient

      // attach message handler to the worker (initial)
      worker.onmessage = (e) => handleInitMessage(e, worker, dbClient)

      // load wasm
      worker.postMessage({ type: 'load_wasm', version: window.appBuilderVersion })
    },
    [handleInitMessage]
  )

  /**
   * Handle close worker
   *
   * Note that we need to ensure clearing all the state incase this hook
   * doesnt re-render when switching to another database/app
   */
  const handleCloseWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate()
      workerRef.current = undefined
      window.dbWorker = null
    }
    if (dbClientRef.current) {
      dbClientRef.current.Close()
      dbClientRef.current = undefined
    }
    if (queueRef.current) {
      queueRef.current.clear()
      queueRef.current = undefined
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }
    initStartedRef.current = false
    requestedTablesRef.current.clear()
    setWorkerStatus(WORKER_STATUS.PENDING)
    setLoadedTables(new Set())
    setSnapshotLoadingSet(new Set())
  }, [])

  /**
   * Handle update dbClient
   *
   * @note we need to keep a separate ref of the dbClient here
   */
  const handleUpdateDbClient = useCallback((dbClient: IDB) => {
    dbClientRef.current = dbClient
  }, [])

  /**
   * Safety net to ensure message handler is always set with current dbClient
   * This also handles cases where dbClient changes after initial setup
   */
  useEffect(() => {
    const worker = workerRef.current

    if (workerStatus === WORKER_STATUS.READY && worker) {
      worker.onmessage = (e) => handleMessage(e)
    }

    if (workerStatus === WORKER_STATUS.ERROR) {
      // reload dbClient
      window.dispatchEvent(new CustomEvent('db-client-reload'))
    }
  }, [handleMessage, workerStatus])

  return {
    /**
     * Loaded tables
     */
    loadedTables,
    tablesLoadingMap,
    wasmProcessing,
    /**
     * Snapshot loading map
     */
    snapshotLoadingSet,
    setSnapshotLoadingSet,
    /**
     * dbWorker instance
     */
    worker: workerRef.current,
    /**
     * DB Worker status
     */
    status: workerStatus,
    /**
     * Init worker
     */
    initDbWorker: handleInitWorker,
    /**
     * Close worker
     */
    closeDbWorker: handleCloseWorker,
    /**
     * Update dbClient
     */
    updateDbClient: handleUpdateDbClient,
  }
}
